import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn.js';

/**
 * Fast, hosting-friendly image loader.
 * - priority: LCP / hero images (eager + fetchPriority high)
 * - deferUntilVisible: skip network until near viewport (best for grids)
 * - decoding async + lazy by default keeps the main thread responsive
 */
export function OptimizedImage({
  src,
  alt = '',
  className,
  style,
  priority = false,
  deferUntilVisible = false,
  fallbackSrc,
  onError,
  onLoad,
  ...rest
}) {
  const ref = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(priority || !deferUntilVisible);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
    setLoaded(false);
    if (priority || !deferUntilVisible) {
      setShouldLoad(true);
      return;
    }

    setShouldLoad(false);
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '320px 0px', threshold: 0.01 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [src, priority, deferUntilVisible]);

  const resolvedSrc = failed && fallbackSrc ? fallbackSrc : src;
  const isVisible = priority || loaded;

  return (
    <img
      ref={ref}
      {...(shouldLoad ? { src: resolvedSrc } : {})}
      alt={alt}
      className={cn(
        isVisible ? 'opacity-100' : 'opacity-0',
        'transition-opacity duration-300',
        className,
      )}
      style={style}
      loading={priority ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : 'auto'}
      decoding="async"
      onLoad={(event) => {
        setLoaded(true);
        onLoad?.(event);
      }}
      onError={(event) => {
        if (fallbackSrc && !failed) {
          setFailed(true);
          setLoaded(false);
        }
        onError?.(event);
      }}
      {...rest}
    />
  );
}
